import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';

import { getCMSFiltersMappedValues } from '../../../../../components/formulas/settings/AttributesFilters/helper';
import BasicModal from '../../../../../components/modals/basicModal';
import styled from 'styled-components';

//constants
import aampeTheme from '../../../../../constants/aampeTheme';
import routes from '../../../../../constants/routes';
import { ModalHeaderStyle } from '../../../../attributes/constants';

//context
import { EditorContext } from '../../../../../context/EditorContext';

import formulaKeys from '../../../../../hooks/formulas/_queryKeys';
import useFormulaCMSFiltersMutation from '../../../../../hooks/formulas/useFormulaCMSFilterMutation';
import useFormulaCreateMutation from '../../../../../hooks/formulas/useFormulaCreateMutation';
import useGetCurrentMessageContent from '../../../../../hooks/message-content/useGetCurrentMessageContent';
import useMessageContentGetAndUpdate from '../../../../../hooks/message-content/useMessageContentGetAndUpdate';

import {
    CartFilterFormValues,
    CMSDisplayNames,
    CMSFilterType,
    CMSType,
    DEFAULT_CMS_TYPE_OPTION,
    DEFAULT_CMS_TYPE_VALUE,
} from '../../../../../models/cms-placeholder.model';
//models
import { FormulaUpdateModel } from '../../../../../models/formula.model';

import { LocationState } from '../../../../../types/location-state';

import CMSPlaceholderFilter from './CMSPlaceholderFilter';
import CMSPlaceholderType from './CMSPlaceholderType';

interface CMSPlaceholderMainModalProps {
    handleClose: () => void;
    isEditable: boolean;
}
const CMSPlaceholderMainModal: FC<CMSPlaceholderMainModalProps> = ({
    handleClose,
    isEditable,
}) => {
    const {
        editorDetails: { formulaType, formula },
    } = useContext(EditorContext);
    const [showCMSPlaceholderFilter, setShowCMSPlaceholderFilter] =
        useState<boolean>(false);

    const history = useHistory<LocationState>();
    const queryClient = useQueryClient();

    const messageCreate = useFormulaCreateMutation(formulaType);
    const messageContentUpdate = useMessageContentGetAndUpdate();
    const { currentMessageContentId } = useGetCurrentMessageContent(
        formula?.id,
    );
    const cmsFilterUpdate = useFormulaCMSFiltersMutation(formula?.id!);
    const initialValues = useMemo<CartFilterFormValues>(
        () => ({
            cms_type: formula?.cms_type
                ? {
                      label: CMSDisplayNames[formula?.cms_type],
                      value: formula?.cms_type,
                  }
                : DEFAULT_CMS_TYPE_OPTION,
            cms_filters: formula?.cms_placeholder_filters ?? [],
            cms_filter_type: formula?.cms_placeholder_filters?.length
                ? CMSFilterType.SPECIFIC
                : CMSFilterType.GENERAL,
        }),
        [formula],
    );

    const handleSuccess = useCallback(
        (formulaId: number) => {
            queryClient.invalidateQueries({ queryKey: formulaKeys.all });
            handleClose();
            if (history.location.pathname === routes.formulasCreate) {
                history.replace(routes.formulaDraft(formulaId), {
                    from: history.location.state?.from,
                });
            }
        },
        [handleClose, history, queryClient],
    );

    const handleFilterButtonClick = useCallback(
        (formikProps: CartFilterFormValues) => {
            const cmsType =
                formikProps?.cms_type?.value === DEFAULT_CMS_TYPE_VALUE
                    ? null
                    : formikProps?.cms_type?.value;
            cmsFilterUpdate.mutate({
                cms_placeholder_filters: getCMSFiltersMappedValues(formikProps),
                message_content_id: currentMessageContentId,
            });
            messageContentUpdate.mutate(
                {
                    id: currentMessageContentId!,
                    cms_type: cmsType as CMSType,
                },
                {
                    onSuccess: () => {
                        handleSuccess(formula?.id!);
                    },
                },
            );
        },
        [
            cmsFilterUpdate,
            formula,
            messageContentUpdate,
            handleSuccess,
            currentMessageContentId,
        ],
    );

    const onSubmit = useCallback(
        (formikProps: CartFilterFormValues) => {
            const cmsType =
                formikProps?.cms_type?.value === DEFAULT_CMS_TYPE_VALUE
                    ? null
                    : formikProps?.cms_type?.value;

            if (formikProps.cms_filter_type === CMSFilterType.SPECIFIC) {
                if (formula?.id) {
                    handleFilterButtonClick(formikProps);
                } else {
                    messageCreate.mutate(
                        {
                            cms_type: cmsType,
                        } as FormulaUpdateModel,
                        {
                            onSuccess: (data) => {
                                cmsFilterUpdate.mutate(
                                    {
                                        formula_id: data.data.id,
                                        cms_placeholder_filters:
                                            getCMSFiltersMappedValues(
                                                formikProps,
                                            ),
                                        message_content_id:
                                            currentMessageContentId,
                                    },
                                    {
                                        onSuccess: () => {
                                            handleSuccess(data.data.id);
                                        },
                                    },
                                );
                            },
                        },
                    );
                }
            } else {
                if (formula?.id) {
                    cmsFilterUpdate.mutate({
                        cms_placeholder_filters: [],
                        message_content_id: currentMessageContentId,
                    });
                    messageContentUpdate.mutate(
                        {
                            id: currentMessageContentId!,
                            cms_type: cmsType as CMSType,
                        },
                        {
                            onSuccess: () => {
                                handleSuccess(formula?.id!);
                                setShowCMSPlaceholderFilter(false);
                            },
                        },
                    );
                } else {
                    messageCreate.mutate(
                        {
                            cms_type: cmsType,
                        } as FormulaUpdateModel,
                        {
                            onSuccess: (data) => handleSuccess(data.data.id),
                        },
                    );
                }
            }
        },
        [
            formula,
            messageCreate,
            cmsFilterUpdate,
            handleSuccess,
            messageContentUpdate,
            handleFilterButtonClick,
            currentMessageContentId,
        ],
    );

    return (
        <BasicModal
            childContainerStyle={{
                padding: aampeTheme.spacing4(6, 8, 4, 8),
            }}
            width={showCMSPlaceholderFilter ? '720px' : '512px'}
            header="Select CMS type"
            handleClose={handleClose}
            headerStyle={ModalHeaderStyle}
        >
            <Formik onSubmit={onSubmit} initialValues={initialValues}>
                {(formikProps) => (
                    <StyledForm>
                        {showCMSPlaceholderFilter ? (
                            <CMSPlaceholderFilter
                                handleClose={() =>
                                    setShowCMSPlaceholderFilter(false)
                                }
                                formikProps={formikProps}
                                isDisabled={!isEditable}
                            />
                        ) : (
                            <CMSPlaceholderType
                                isDisabled={!isEditable}
                                formikProps={formikProps}
                                handleClose={handleClose}
                                OnNextClick={() =>
                                    setShowCMSPlaceholderFilter(true)
                                }
                            />
                        )}
                    </StyledForm>
                )}
            </Formik>
        </BasicModal>
    );
};

const StyledForm = styled(Form)({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(8),
});

export default CMSPlaceholderMainModal;
